"use client";

// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → now central widget_impression
// (TrackedWidget in NewPage); see webapp/docs/analytics
// import { FireWidgetViewedEvent } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { ArrowUpRight } from "lucide-react";
import Image from "next/image";
import { useRef } from "react";

export interface CareerProspects2Data {
  id?: string | number;
  status?: boolean;
  title?: string;
  title_text_color?: string;
  sub_title?: string;
  sub_title_text_color?: string;
  note_text?: string;
  note_text_color?: string;
  note_italic?: boolean;
  background_color?: string;
  certificate_image?: string;
  certificate_image_alt?: string;
  cta_text?: string;
  cta_text_color?: string;
  cta_background_color?: string;
  cta_url?: string;
  cta_icon?: string;
  cta_icon_alt?: string;
}

interface CareerProspects2Props {
  data: CareerProspects2Data | { data: CareerProspects2Data };
}

function resolvePayload(
  props: CareerProspects2Props["data"],
): CareerProspects2Data | undefined {
  const anyData = props as { data?: CareerProspects2Data };
  if (anyData?.data && typeof anyData.data === "object") {
    return anyData.data as CareerProspects2Data;
  }
  return props as CareerProspects2Data;
}

const DEFAULT_TITLE = "Beyond a Certificate – Built for Career Impact";
const DEFAULT_SUB =
  "Earn recognised credentials and academic recognition.";
const DEFAULT_NOTE =
  "Top 10 percentile performers in each cohort receive a Certificate with Distinction, recognised on the credential itself.";

function CareerProspects2({ data }: CareerProspects2Props) {
  const sectionData = resolvePayload(data);
  const ref = useRef<HTMLElement>(null);

  const {
    status = true,
    title = DEFAULT_TITLE,
    title_text_color = "#000000",
    sub_title = DEFAULT_SUB,
    sub_title_text_color = "#000000",
    note_text = DEFAULT_NOTE,
    note_text_color = "#000000",
    note_italic = true,
    background_color = "#FFFFFF",
    certificate_image = "",
    certificate_image_alt = "Certificate",
    cta_text = "",
    cta_text_color = "#FFFFFF",
    cta_background_color = "#DB5803",
    cta_url = "",
    cta_icon = "",
    cta_icon_alt = "",
  } = sectionData || {};

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "career_prospects_2_section");
  //   }
  // }, []);

  if (status === false) {
    return null;
  }

  const hasCertImage =
    typeof certificate_image === "string" && certificate_image.trim() !== "";

  return (
    <section
      ref={ref}
      className={cn(
        "[&_*]:![font-family:Poppins,sans-serif]",
        "mx-auto flex w-full items-center justify-center",
      )}
      style={{
        backgroundColor: background_color,
        fontFamily: "Poppins, sans-serif",
      }}
    >
      <div className="flex w-full flex-col items-center justify-center">
        <div
          className={cn(
            "flex w-full flex-col items-center justify-center gap-8",
            "pt-8 pb-12 min-[1024px]:pt-11 min-[1024px]:pb-[72px]",
            "px-[24px] min-[1024px]:pl-[64px] min-[1024px]:pr-[103px]",
            hasCertImage && "min-[1024px]:flex-row min-[1024px]:gap-[103px]",
          )}
        >
          <div
            className={cn(
              "flex w-full flex-col gap-[12px] text-center min-[1024px]:w-[442px] min-[1024px]:gap-[27px] min-[1024px]:text-left",
            )}
          >
            {title ? (
              <div className="mx-auto w-[283px] min-[1024px]:mx-0 min-[1024px]:w-full">
                <h2
                  className="whitespace-pre-line text-[28px] font-bold leading-tight text-primary-dark-100"
                  style={{ color: title_text_color }}
                >
                  {title}
                </h2>
              </div>
            ) : null}
            {sub_title ? (
              <div className="mx-auto w-[283px] min-[1024px]:mx-0 min-[1024px]:w-full">
                <p
                  className="text-[16px] font-normal leading-relaxed text-primary-dark-100 min-[1024px]:text-[18px]"
                  style={{ color: sub_title_text_color }}
                >
                  {sub_title}
                </p>
              </div>
            ) : null}
            {note_text ? (
              <div>
                <p
                  className={cn(
                    "text-[12px] font-normal leading-relaxed text-primary-dark-100 min-[1024px]:text-[16px]",
                    note_italic && "italic",
                  )}
                  style={{ color: note_text_color }}
                >
                  {note_text}
                </p>
              </div>
            ) : null}
            {cta_text ? (
              <div className="mx-auto min-[1024px]:mx-0">
                <a
                  href={cta_url || undefined}
                  target={cta_url ? "_blank" : undefined}
                  rel={cta_url ? "noopener noreferrer" : undefined}
                  className={cn(
                    "inline-flex items-center justify-center gap-2",
                    "h-[48px] rounded-[8px] px-6",
                    "text-[14px] font-semibold min-[1024px]:text-[16px]",
                  )}
                  style={{
                    backgroundColor: cta_background_color,
                    color: cta_text_color,
                  }}
                >
                  {cta_text}
                  {cta_icon ? (
                    <span className="relative inline-block size-5 shrink-0">
                      <Image
                        src={cta_icon}
                        alt={cta_icon_alt || "CTA"}
                        fill
                      />
                    </span>
                  ) : (
                    <ArrowUpRight
                      className="h-5 w-5 shrink-0"
                      strokeWidth={2}
                      aria-hidden
                    />
                  )}
                </a>
              </div>
            ) : null}
          </div>

          {hasCertImage ? (
            <div className="shrink-0">
              <Image
                src={certificate_image.trim()}
                alt={certificate_image_alt || "Certificate"}
                width={401}
                height={442}
                className={cn(
                  "h-[234px] w-auto object-cover",
                  "rounded-[20px] border-[1px] border-secondary-dark-800",
                  "min-[1024px]:h-[284px] min-[1024px]:w-[400px] min-[1024px]:rounded-none",
                )}
              />
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

export default CareerProspects2;
